import { useEffect, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

// ── Session flag — run checks once per session ────────────────────────────────
const CHECKED_KEY = 'panda_smart_notif_checked';

const wasCheckedThisSession = (userId: string): boolean => {
  try {
    const raw = sessionStorage.getItem(CHECKED_KEY);
    if (!raw) return false;
    const data = JSON.parse(raw) as Record<string, string>;
    const today = new Date().toISOString().split('T')[0];
    return data[userId] === today;
  } catch {
    return false;
  }
};

const markChecked = (userId: string) => {
  try {
    const raw = sessionStorage.getItem(CHECKED_KEY);
    const data = raw ? (JSON.parse(raw) as Record<string, string>) : {};
    data[userId] = new Date().toISOString().split('T')[0];
    sessionStorage.setItem(CHECKED_KEY, JSON.stringify(data));
  } catch { /* ignore */ }
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const notifExists = async (userId: string, type: string, contextKey: string): Promise<boolean> => {
  const today = new Date().toISOString().split('T')[0];
  const { count } = await supabase
    .from('notifications')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('type', type)
    .gte('created_at', today)
    .contains('data', { context_key: contextKey });
  return (count ?? 0) > 0;
};

const insertNotification = async (
  userId: string,
  type: string,
  title: string,
  body: string,
  contextKey: string,
  extraData?: Record<string, unknown>
) => {
  const exists = await notifExists(userId, type, contextKey);
  if (exists) return;
  await supabase.from('notifications').insert({
    user_id: userId,
    type,
    title,
    body,
    data: { context_key: contextKey, ...(extraData ?? {}) },
  }).then(() => {/* ignore error */}).catch(() => {/* ignore */});
};

// ── Hook ───────────────────────────────────────────────────────────────────────
export const useSmartNotifications = () => {
  const { user } = useAuth();
  const ranRef = useRef(false);

  const runChecks = useCallback(async () => {
    if (!user || ranRef.current) return;
    if (wasCheckedThisSession(user.id)) return;

    ranRef.current = true;
    markChecked(user.id);

    const now = new Date();
    const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
    const thisMonthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
    const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString().split('T')[0];
    const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0).toISOString().split('T')[0];
    const todayStr = now.toISOString().split('T')[0];
    const in3Days = new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

    try {
      // Run all queries in parallel
      const [thisMoRes, lastMoRes, goalsRes, billsRes] = await Promise.allSettled([
        // This month expenses
        supabase
          .from('transactions')
          .select('amount')
          .eq('user_id', user.id)
          .eq('type', 'despesa')
          .eq('status', 'pago')
          .neq('transaction_subtype', 'transferencia')
          .gte('date', thisMonthStart)
          .lte('date', thisMonthEnd)
          .is('deleted_at', null),

        // Last month expenses
        supabase
          .from('transactions')
          .select('amount')
          .eq('user_id', user.id)
          .eq('type', 'despesa')
          .eq('status', 'pago')
          .neq('transaction_subtype', 'transferencia')
          .gte('date', lastMonthStart)
          .lte('date', lastMonthEnd)
          .is('deleted_at', null),

        // Goals near completion (>= 80% funded)
        supabase
          .from('goals')
          .select('id, name, current_amount, target_amount')
          .eq('user_id', user.id)
          .eq('is_completed', false)
          .eq('status', 'active'),

        // Bills due in next 3 days
        supabase
          .from('transactions')
          .select('id, description, amount, date')
          .eq('user_id', user.id)
          .eq('type', 'despesa')
          .in('status', ['pendente', 'vencido'])
          .gte('date', todayStr)
          .lte('date', in3Days)
          .is('deleted_at', null)
          .order('date', { ascending: true })
          .limit(5),
      ]);

      // ── 1. Spending increase alert ─────────────────────────────────────────
      if (thisMoRes.status === 'fulfilled' && lastMoRes.status === 'fulfilled') {
        const thisExpenses = (thisMoRes.value.data ?? []).reduce((s: number, t: { amount: number }) => s + Number(t.amount), 0);
        const lastExpenses = (lastMoRes.value.data ?? []).reduce((s: number, t: { amount: number }) => s + Number(t.amount), 0);

        if (lastExpenses > 0 && thisExpenses > 0) {
          const pctChange = Math.round(((thisExpenses - lastExpenses) / lastExpenses) * 100);
          if (pctChange >= 20) {
            await insertNotification(
              user.id,
              'warning',
              `Gastos subiram ${pctChange}% este mês`,
              `Você gastou ${pctChange}% a mais em comparação com o mês passado. Confira seus lançamentos para entender onde está o aumento.`,
              `spending_increase_${thisMonthStart}`,
              { pct_change: pctChange }
            );
          }
        }
      }

      // ── 2. Goals near completion ────────────────────────────────────────────
      if (goalsRes.status === 'fulfilled') {
        const goals = (goalsRes.value.data ?? []) as { id: string; name: string; current_amount: number; target_amount: number }[];
        for (const goal of goals) {
          const pct = goal.target_amount > 0
            ? (Number(goal.current_amount) / Number(goal.target_amount)) * 100
            : 0;
          if (pct >= 80 && pct < 100) {
            await insertNotification(
              user.id,
              'goal_completed',
              `Meta "${goal.name}" quase lá! 🎯`,
              `Você atingiu ${Math.round(pct)}% da meta. Falta pouco para concluí-la!`,
              `goal_near_${goal.id}_${thisMonthStart}`,
              { goal_id: goal.id, pct: Math.round(pct) }
            );
            break; // max 1 goal notification per day
          }
        }
      }

      // ── 3. Bills due very soon (≤ 3 days) ─────────────────────────────────
      if (billsRes.status === 'fulfilled') {
        const bills = (billsRes.value.data ?? []) as { id: string; description: string; amount: number; date: string }[];
        if (bills.length > 0) {
          const urgentBill = bills[0];
          const dueDate = new Date(urgentBill.date + 'T00:00:00');
          const today = new Date(todayStr + 'T00:00:00');
          const daysLeft = Math.round((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
          const daysLabel = daysLeft === 0 ? 'hoje' : daysLeft === 1 ? 'amanhã' : `em ${daysLeft} dias`;

          await insertNotification(
            user.id,
            'calendar_pending',
            `Conta vence ${daysLabel}`,
            `"${urgentBill.description}" vence ${daysLabel} — R$ ${Number(urgentBill.amount).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}. Não esqueça de pagar.`,
            `bill_due_${urgentBill.id}_${todayStr}`,
            { transaction_id: urgentBill.id, days_left: daysLeft }
          );
        }
      }
    } catch {
      // Silent failure — notifications are non-critical
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    // Delay 3s to avoid competing with initial page load
    const timer = setTimeout(() => { runChecks(); }, 3000);
    return () => clearTimeout(timer);
  }, [user, runChecks]);
};
